import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
    enableProdMode,
    importProvidersFrom,
    inject,
    provideEnvironmentInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgSelectModule } from '@ng-select/ng-select';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
// 3rd party packages
import { HighchartsChartModule } from 'highcharts-angular';

// @priva packages
import {
    PrivaSegmentConfig,
    PrivaSegmentCoreModule,
    PrivaSegmentService,
    PrivaSegmentUserService,
} from '@priva/analytics/segment';
import { AppShellModule, AppShellUtilitiesModule, PrimaryNavigationService } from '@priva/appshell';
import { PrivaAuthCurrentUserBffModule, PrivaAuthCurrentUserService } from '@priva/auth/current-user';
import { PrivaAuthHttpBffModule, PrivaAuthHttpOptions } from '@priva/auth/http';
import { PrivaErrorPageHandlerService } from '@priva/error-pages';
import { DateTimeFormatPipe, providePrivaLocalization } from '@priva/localization';
import { PrivaNotificationsService } from '@priva/notifications';
import { PrivaPreferencesOptions, providePrivaPreferences } from '@priva/preferences/core';
import { PrivaPreloaderElement } from '@priva/preloader';
import { PrivaToggleRouter } from '@priva/utilities/feature-toggles';
import { AppConfiguration } from '@priva/utilities/layout';
import { PrivaScrollToModule } from '@priva/utilities/scrollto';
import { PrivaToggleModule } from '@priva/utilities/toggle';

// Routing
import { AppRoutingModule } from 'app/app-routing.module';
// Self
import { AppComponent } from 'app/app.component';
import {
    getAnalyticsSegmentConfig,
    getAppShellConfig,
    getCustomAuthOptions,
    getPreferencesOptions,
    getToggleRouter,
    localizationOptions,
    metaReducers,
} from 'app/app.config';
import { CurrentUserModule } from 'app/current-user/current-user.module';
import { KpiNotificationSettingsModule } from 'app/kpi-notification-settings/kpi-notification-settings.module';
import { AccessControlService } from 'app/shared/access-control';
import { AccessControlConfiguration } from 'app/shared/access-control/configuration';
import { TrackEventsService } from 'app/shared/analytics';
import { AppConfigurationModule, AppConfigurationService } from 'app/shared/app-configuration';
import { ErrorHandlerService, HttpErrorHandlingService } from 'app/shared/error-handling';
import { ToggleRouterModule } from 'app/shared/feature-toggles';
import { HelpInfoModule } from 'app/shared/help';
import { LoadingModule } from 'app/shared/loading';
import { RecurrencyOptionsService } from 'app/shared/recurrency-options';
import { ReportModelModule } from 'app/shared/report-model';
import { ReportPeriodModule } from 'app/shared/report-period';
import { ReportAppEffects } from 'app/state/app.effects';
import { reducer } from 'app/state/app.reducer';
import { AppPrimaryNavigationService } from 'shared/routing-proxies/primary-navigation.service';
import { SiteApiEffects } from 'shared/site/site-api.effects';
import { SiteModule } from 'shared/site/site.module';

// Environment
import { environment } from './environments/environment';

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),
        importProvidersFrom(
            BrowserAnimationsModule,
            NgSelectModule,
            AppRoutingModule,
            PrivaAuthCurrentUserBffModule.forRoot(),
            PrivaAuthHttpBffModule.forRoot({
                provide: PrivaAuthHttpOptions,
                useFactory: getCustomAuthOptions,
                deps: [AppConfigurationService],
            }),
            CurrentUserModule,
            KpiNotificationSettingsModule,
            SiteModule,
            AppConfigurationModule.forRoot(),
            AppShellModule.forRoot(
                {
                    provide: AppConfiguration,
                    useFactory: getAppShellConfig,
                    deps: [AppConfigurationService],
                },
                [{ provide: PrimaryNavigationService, useClass: AppPrimaryNavigationService }],
            ),
            AppShellUtilitiesModule,
            HelpInfoModule.forRoot(),
            ToggleRouterModule.forRoot(),
            PrivaToggleModule.forRoot(),
            PrivaScrollToModule.forRoot(),
            PrivaSegmentCoreModule.forRoot([
                // Segment config
                {
                    provide: PrivaSegmentConfig,
                    useFactory: getAnalyticsSegmentConfig,
                    deps: [AppConfigurationService],
                },
                // required: provide a user service to identify the user
                {
                    provide: PrivaSegmentUserService,
                    useExisting: PrivaAuthCurrentUserService,
                },
            ]),
            ReportModelModule.forRoot(),
            ReportPeriodModule.forRoot(),
            FormsModule,
            ReactiveFormsModule,
            StoreModule.forRoot(
                { app: reducer, router: routerReducer },
                {
                    metaReducers,
                    runtimeChecks: {
                        strictStateImmutability: false,
                        strictActionImmutability: false,
                    },
                },
            ),
            StoreDevtoolsModule.instrument({
                name: 'Priva Analytics App',

                // In a production build you would want to disable the Store Devtools
                // logOnly: environment.production,
                connectInZone: true,
            }),
            EffectsModule.forRoot([ReportAppEffects, SiteApiEffects]),
            LoadingModule,
            HighchartsChartModule,
        ),
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: PrivaToggleRouter, useFactory: getToggleRouter, deps: [AppConfigurationService] },
        ErrorHandlerService,
        TrackEventsService,
        HttpErrorHandlingService,
        PrivaErrorPageHandlerService,
        RecurrencyOptionsService,
        DateTimeFormatPipe,
        AccessControlService,
        AccessControlConfiguration,
        PrivaNotificationsService,
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        providePrivaLocalization(localizationOptions),
        providePrivaPreferences({
            provide: PrivaPreferencesOptions,
            useFactory: getPreferencesOptions,
            deps: [AppConfigurationService],
        }),
        // Eagerly instantiate the segment service (previously done via the AppModule constructor)
        provideEnvironmentInitializer(() => {
            inject(PrivaSegmentService);
        }),
    ],
})
    .then(() => {
        // trigger preloader
        document.querySelector<PrivaPreloaderElement>('priva-preloader')?.appBootstrap();
    })
    // eslint-disable-next-line no-console
    .catch((err) => console.log(err));
